import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Menu, X } from "lucide-react";

export default function Navbar() {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const navLinks = [
    { to: "/", label: t("nav.home"), testId: "nav-home-link" },
    { to: "/about", label: t("nav.about"), testId: "nav-about-link" },
    { to: "/services", label: t("nav.services"), testId: "nav-services-link" },
    { to: "/home-health", label: t("nav.home_health"), testId: "nav-home-health-link" },
    { to: "/lifestyle", label: t("nav.lifestyle"), testId: "nav-lifestyle-link" },
    { to: "/contact", label: t("nav.contact"), testId: "nav-contact-link" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FDFBF7]/80 border-b border-[#E2E0D9]/50 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-logo"
            className="font-heading text-2xl font-semibold text-[#1E2922] tracking-tight"
          >
            Dr. Burcu Varol
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={link.testId}
                className={`text-sm font-medium transition-colors duration-200 ${
                  isActive(link.to)
                    ? "text-[#4A6753]"
                    : "text-[#566D60] hover:text-[#4A6753]"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-4">
            <div
              data-testid="lang-switcher"
              className="flex items-center text-sm font-medium px-3 py-1.5 rounded-full border border-[#E2E0D9]"
            >
              <button
                data-testid="lang-switch-tr"
                onClick={() => setLang("tr")}
                className={`transition-colors ${lang === "tr" ? "text-[#4A6753] font-semibold" : "text-[#566D60] hover:text-[#4A6753]"}`}
              >
                TR
              </button>
              <span className="mx-1.5 text-[#E2E0D9]">|</span>
              <button
                data-testid="lang-switch-en"
                onClick={() => setLang("en")}
                className={`transition-colors ${lang === "en" ? "text-[#4A6753] font-semibold" : "text-[#566D60] hover:text-[#4A6753]"}`}
              >
                EN
              </button>
            </div>
            <Link
              to="/contact"
              data-testid="nav-contact-cta"
              className="bg-[#4A6753] text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-[#3A5242] transition-colors shadow-sm"
            >
              {t("nav.contact")}
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 text-[#1E2922]"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#FDFBF7]/95 backdrop-blur-xl border-t border-[#E2E0D9]/50"
        >
          <div className="px-6 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`mobile-${link.testId}`}
                className={`block text-base font-medium transition-colors ${
                  isActive(link.to)
                    ? "text-[#4A6753]"
                    : "text-[#566D60] hover:text-[#4A6753]"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-[#E2E0D9] flex items-center gap-4">
              <div className="flex items-center gap-2">
                <button
                  data-testid="mobile-lang-switch-tr"
                  onClick={() => setLang("tr")}
                  className={`text-sm font-medium transition-colors px-3 py-1.5 rounded-full border ${lang === "tr" ? "border-[#4A6753] text-[#4A6753] font-semibold bg-[#4A6753]/5" : "border-[#E2E0D9] text-[#566D60]"}`}
                >
                  TR
                </button>
                <button
                  data-testid="mobile-lang-switch-en"
                  onClick={() => setLang("en")}
                  className={`text-sm font-medium transition-colors px-3 py-1.5 rounded-full border ${lang === "en" ? "border-[#4A6753] text-[#4A6753] font-semibold bg-[#4A6753]/5" : "border-[#E2E0D9] text-[#566D60]"}`}
                >
                  EN
                </button>
              </div>
              <Link
                to="/contact"
                data-testid="mobile-contact-cta"
                className="bg-[#4A6753] text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-[#3A5242] transition-colors"
              >
                {t("nav.contact")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
